import React, { useEffect, useRef } from "react";

/**
 * Winamp-style frequency visualizer.
 * - If getFrequencyData is available, uses Web Audio API
 * - Otherwise, falls back to pseudo-random bars that breathe while playing
 */
export default function Visualizer({ getFrequencyData, isPlaying, bars = 28, height = 44 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const fallbackPhaseRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width = canvas.clientWidth * dpr;
    const H = canvas.height = canvas.clientHeight * dpr;
    const barWidth = W / bars;
    const gap = 2 * dpr;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      // background
      ctx.fillStyle = "#05060A";
      ctx.fillRect(0, 0, W, H);

      // grid lines (CRT feel)
      ctx.strokeStyle = "rgba(57,255,20,0.08)";
      ctx.lineWidth = 1;
      for (let y = 0; y < H; y += 4 * dpr) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }

      const freq = getFrequencyData && getFrequencyData();
      fallbackPhaseRef.current += 0.08;
      const phase = fallbackPhaseRef.current;

      for (let i = 0; i < bars; i++) {
        let v;
        if (freq) {
          // Map bar i to frequency bin
          const binIdx = Math.floor((i / bars) * freq.length);
          v = freq[binIdx] / 255;
        } else if (isPlaying) {
          v = 0.25 + 0.45 * Math.abs(Math.sin(phase + i * 0.35)) + Math.random() * 0.2;
          v = Math.min(1, v);
        } else {
          v = 0.04 + Math.random() * 0.03;
        }

        const barHeight = Math.max(2 * dpr, v * (H - 6 * dpr));
        const x = i * barWidth + gap / 2;
        const y = H - barHeight - 2 * dpr;
        const w = barWidth - gap;

        // Gradient green -> yellow -> red
        const grd = ctx.createLinearGradient(0, y, 0, H);
        grd.addColorStop(0, "#FF3030");
        grd.addColorStop(0.25, "#FFEB3B");
        grd.addColorStop(0.55, "#B6FF00");
        grd.addColorStop(1, "#1f8020");
        ctx.fillStyle = grd;
        ctx.fillRect(x, y, w, barHeight);

        // Peak cap
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(x, y - 2 * dpr, w, 2 * dpr);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [getFrequencyData, isPlaying, bars]);

  return (
    <div className="crt-overlay bevel-in" style={{ height }} data-testid="visualizer">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
